// Browser-side GPS simulator. The server picks its GNSS source at boot (sim / phone / serial), so on
// a phone- or cloud-driven deployment there's no simulator to turn on. This runs the SAME pure-TS
// `TractorSimulator` (@fla/simulator) in the browser and feeds fixes straight into the store — a
// dev/testing aid so the whole drive loop is exercisable on a desktop with no GPS. It mirrors the
// server's `SimControls` (apps/server/src/gnss.ts) so the driving flow drives it identically.
import { TractorSimulator } from '@fla/simulator';
import type { GnssFix, LonLat } from '@fla/core';

const FIX_HZ = 5;
const DT = 1 / FIX_HZ;
const INTERVAL_MS = 1000 / FIX_HZ;

class ClientSim {
  private sim: TractorSimulator | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private onFix: ((fix: GnssFix) => void) | null = null;

  /** True while the browser sim is the live fix source. */
  get active(): boolean {
    return this.timer !== null;
  }

  /** Start emitting fixes from `center` (stationary; drive() sets it moving). Idempotent. */
  start(center: LonLat, onFix: (fix: GnssFix) => void): void {
    this.stop();
    this.onFix = onFix;
    this.sim = new TractorSimulator({ start: center, startTimestamp: Date.now(), speedMps: 0 });
    this.timer = setInterval(() => {
      if (this.sim) this.onFix?.(this.sim.step(DT));
    }, INTERVAL_MS);
    this.onFix(this.sim.current()); // show the tractor immediately, before the first tick
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.sim = null;
    this.onFix = null;
  }

  // --- SimControls (same shape as the server source) ---
  drive(path: LonLat[], opts: { speedMps?: number; offsetM?: number } = {}): void {
    if (!this.sim) return;
    this.sim.followPath(path, { offsetM: opts.offsetM ?? 0 });
    this.sim.setSpeed(opts.speedMps ?? 2.5);
  }
  setOffset(m: number): void {
    this.sim?.setOffset(m);
  }
  nudge(delta: number): void {
    this.sim?.nudge(delta);
  }
  setSpeed(mps: number): void {
    this.sim?.setSpeed(mps);
  }
  stopDriving(): void {
    this.sim?.setSpeed(0);
  }
}

/** Process-wide singleton — there's only ever one simulated tractor. */
export const clientSim = new ClientSim();
